import { useCallback, useEffect, useState } from "react";
import { isEmpty, sortBy } from "lodash";
import { useApi } from "@services/useApi";
import { LCheckbox } from "@components";
import { LSelect, SelectOption } from "@components/LSelect";
import { longLocalizedDate } from "@helpers/dateHelpers";
import { FormProvider, useForm } from "react-hook-form";
import { Loader } from "@components/Loader";
import { AssessmentHistory } from "@store/generalTypes";
import { ExamineeContactInfo } from "../../../../store/newExamineeSlice";
import { useModal } from "@services/ModalContext";
import { StartAssessmentStepNames, setStartAssessmentContext } from "../../../../store/startAssessmentSlice";
import { setAssessmentForms, setAssessmentHistoryByExaminee } from "../../../../store/assessmentContextSlice";
import { boolean, number, object, SchemaOf } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch, useAppSelector } from "@app/hooks";

interface StartAssessmentForm {
    assessmentFormId: number;
    isRetest?: boolean;
}

const formSchema: SchemaOf<StartAssessmentForm> = object({
    assessmentFormId: number().required(),
    isRetest: boolean().optional()
});

export const ChooseAssessmentForm = ({
    examinee,
    locationId,
    upcomingAssessmentId
}: ChooseAssessmentFormProps): JSX.Element => {
    const assessmentContext = useAppSelector((x) => x.assessmentContext);

    const [isLoading, setIsLoading] = useState(true);
    const [assessmentFormOptions, setAssessmentFormOptions] = useState<SelectOption[]>([]);
    const [assessmentHistory, setAssessmentHistory] = useState<AssessmentHistory[]>(
        assessmentContext.examineeAssessmentHistory[examinee.id]
    );

    const dispatch = useAppDispatch();
    const validateMethods = useForm<StartAssessmentForm>({
        resolver: yupResolver(formSchema)
    });
    const { setFormName, onConfirm } = useModal();
    const { getAssessmentForms, getAssessmentHistoryByExaminee } = useApi();

    useEffect(() => {
        setFormName?.(StartAssessmentStepNames.AssessmentForm);
        void loadForm();
    }, []);

    const loadForm = useCallback(async () => {
        await loadAssessmentForms();
        await loadHistory();

        setIsLoading(false);
    }, []);

    const loadAssessmentForms = async (): Promise<void> => {
        let forms = assessmentContext.assessmentForms;

        if (isEmpty(forms)) {
            forms = await getAssessmentForms();
            dispatch(setAssessmentForms(forms));
        }

        const options = forms.map((af) => ({
            name: af.name,
            value: af.id.toString()
        }));

        setAssessmentFormOptions(sortBy(options, "name"));
    };

    const loadHistory = async (): Promise<void> => {
        if (isEmpty(assessmentHistory)) {
            const history = await getAssessmentHistoryByExaminee(locationId, examinee.id);

            dispatch(setAssessmentHistoryByExaminee({ examineeId: examinee.id, assessmentHistory: history }));

            setAssessmentHistory(history);
        }
    };

    const handleOnSubmit = (data: StartAssessmentForm): void => {
        dispatch(
            setStartAssessmentContext({
                ...data,
                examineeId: examinee.id,
                locationId,
                upcomingAssessmentId
            })
        );

        onConfirm?.();
    };

    const renderForm = (): JSX.Element => {
        return (
            <FormProvider {...validateMethods}>
                <form
                    id={StartAssessmentStepNames.AssessmentForm}
                    onSubmit={validateMethods.handleSubmit(handleOnSubmit)}
                >
                    <div className="mt-5">
                        <LSelect
                            options={assessmentFormOptions}
                            label="Select Version"
                            inputName="assessmentFormId"
                            defaultValue={assessmentFormOptions[0].name}
                            isFullWidth
                            tooltip="We recommend testing Form A first, followed by Form B, then Form C. Repeat as needed."
                        />
                    </div>

                    <div className="is-flex is-flex-direction-row is-justify-content-space-between">
                        <div>This is a retest</div>
                        <LCheckbox inputName="isRetest" />
                    </div>

                    <div className="fatDivider my-5" />

                    {!isEmpty(assessmentHistory) && (
                        <div>
                            <p className="has-text-weight-bold mb-4">Test Version History</p>
                            {assessmentHistory.map((h) => {
                                return (
                                    <div key={h.id} className="columns pl-3">
                                        <p className="column p-0 is-3">{h.assessmentFormName}</p>
                                        <p className="column p-0">{longLocalizedDate(h.launchedDate)}</p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </form>
            </FormProvider>
        );
    };

    return (
        <div className="container has-text-centered">
            <div className="mw-40 has-text-left">{isLoading ? <Loader /> : renderForm()}</div>
        </div>
    );
};

interface ChooseAssessmentFormProps {
    examinee: ExamineeContactInfo;
    locationId: number;
    upcomingAssessmentId?: number;
}
